<script setup lang='ts'>
import { faArrowLeft, faArrowRight } from '@fortawesome/pro-regular-svg-icons'

const props = defineProps<{
  disabled?: boolean
  currentPage: number
  itemsPerPage: number
  itemCount: number
}>()

const emit = defineEmits(['goToPage'])

const maxVisibleButtons = ref(3)

const startPage = computed(() => Math.max(1, props.currentPage - Math.floor(maxVisibleButtons.value / 2)))

const totalPages = computed(() => Math.ceil(props.itemCount / props.itemsPerPage))

const offset = computed(() => (props.currentPage - 1) * props.itemsPerPage)

const pages = computed(() => {
  const range = []

  for (let i = startPage.value; i <= Math.min(startPage.value + maxVisibleButtons.value - 1, totalPages.value); i++) {
    range.push(i)
  }

  return range
})

const hasPrevPage = computed(() => props.currentPage > 1)

const hasNextPage = computed(() => offset.value + props.itemsPerPage < props.itemCount)

function goToFirstPage() {
  emit('goToPage', 1)
}

function goToPrevPage() {
  emit('goToPage', props.currentPage - 1)
}

function goToNextPage() {
  emit('goToPage', props.currentPage + 1)
}

function gotToExactPage(page: number) {
  emit('goToPage', page)
}

function goToLastPage() {
  emit('goToPage', totalPages.value)
}
</script>

<template>
  <nav role="navigation" aria-label="Sidenavigasjon">
    <ul class="flex gap-s items-center">
      <li v-if="hasPrevPage">
        <Button variant="tertiary" size="medium" :disabled="disabled" class="!bg-transparent" aria-label="Gå til forrige side" @click.prevent="goToPrevPage">
          <Shape variant="hexagon" :icon="faArrowLeft" class="w-9" />
        </Button>
      </li>

      <li v-if="currentPage >= maxVisibleButtons">
        <button
          aria-label="Side 1"
          class="w-8 h-8 bg-blurple-100 text-blurple-500 hover:bg-blurple-50 active:bg-blurple-200 rounded-full"
          :aria-current="currentPage === 1 "
          @click.prevent="goToFirstPage"
        >
          1
        </button>
      </li>

      <li v-if="currentPage >= maxVisibleButtons + 1" aria-hidden="true" class="pagination-dots">
        <div v-for="(_, key) in Array.from(Array(3))" :key="key" />
      </li>

      <li v-for="pageNumber in pages" :key="`page-${pageNumber}`">
        <button
          :aria-label="`Side ${pageNumber}`"
          class="w-8 h-8 bg-blurple-100 text-blurple-500 hover:bg-blurple-50 active:bg-blurple-200 rounded-full"
          :aria-current="currentPage === pageNumber"
          :aria-labelledby="`page-${pageNumber}`"
          @click.prevent="gotToExactPage(pageNumber)"
        >
          {{ pageNumber }}
        </button>
      </li>

      <li v-if="!pages.includes(totalPages)" aria-hidden="true" class="pagination-dots">
        <div v-for="(_, key) in Array.from(Array(3))" :key="key" />
      </li>

      <li v-if="!pages.includes(totalPages)">
        <button
          :aria-label="`Side ${totalPages}`"
          class="w-8 h-8 bg-blurple-100 text-blurple-500 hover:bg-blurple-50 active:bg-blurple-200 rounded-full"
          :aria-current="currentPage === totalPages"
          :aria-labelledby="`page-${totalPages}`"
          @click.prevent="goToLastPage"
        >
          {{ totalPages }}
        </button>
      </li>

      <li v-if="hasNextPage">
        <Button variant="tertiary" size="medium" :disabled="disabled" class="!bg-transparent" aria-label="Gå til neste side" @click.prevent="goToNextPage">
          <Shape variant="hexagon" :icon="faArrowRight" class="w-9" />
        </Button>
      </li>
    </ul>
  </nav>
</template>

<style scoped lang='postcss'>
ul li {
  @apply list-none;

  &.pagination-dots {
    @apply flex gap-[3px] items-center text-blurple-50;

    > div {
      @apply w-[3px] h-[3px] bg-blurple-200 rounded-full;
    }
  }

  > button {
    @apply p-0 focus:outline-blurple-300 disabled:bg-transparent;

    > svg {
      @apply fill-blurple-100 text-blurple-500;
    }

    &:hover {
      > svg {
        @apply fill-blurple-50;
      }
    }

    &:disabled {
      > svg {
        @apply fill-carbon-100 text-carbon-400;
      }
    }

    &:active {
      > svg {
        @apply fill-blurple-200;
      }
    }
  }

  > button[aria-current='false'] {
    @apply bg-transparent !important;

    &:hover {
      @apply bg-blurple-50 !important;
    }
  }

  > button[aria-current='true'] {
    @apply bg-blurple-100 hover:bg-blurple-50 active:bg-blurple-200 !important;
  }
}
</style>
